import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { generatePath } from 'react-router';
import * as yup from 'yup';
import { BaseError } from '~/components/Error/BaseError';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useSearchParams } from '~/components/Route';

export type TransactionType = 'ALL' | 'CHARGE' | 'USE';

export const schema = yup.object().shape({
  transactionType: yup.string().oneOf(['ALL', 'CHARGE', 'USE']).default('ALL'),
});

export type DonHistorySearchParams = {
  transactionType: TransactionType;
};

export const useDonHistoryParams = () => {
  const converter = useCallback((params) => schema.castOrDefault(params), []);
  return useSearchParams<DonHistorySearchParams>({ converter });
};

export type DonHistoriesParams = DonHistorySearchParams & {
  pageNo?: number;
};

export type DonHistory = {
  id: number;
  assetType: string;
  // xlt key
  reasonType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reasonDetail?: Record<string, any>;
  amount: number;
  resultAmount: number;
  createdAt: number;
};

export type DonHistoriesResponse = {
  pageNo: number;
  last: boolean;
  totalCount: number;
  transactionList: DonHistory[];
};

export const donHistoriesUrl = '/api/citizen/v1/balance/transactions';

const getDonHistories = async (params: DonHistoriesParams) => {
  const { data } = await axios.get<DonHistoriesResponse>(donHistoriesUrl, { params });
  if (data.totalCount === 0 && data.pageNo === 1) {
    throw new BaseError('EMPTY');
  }
  return data;
};

export const useDonHistories = (
  params?: DonHistoriesParams,
  options?: UseInfiniteQueryOptions<DonHistoriesResponse>
) => {
  const transactionType = params?.transactionType || 'CHARGE';
  return useInfiniteQuery({
    queryKey: ['don', 'history', { transactionType }],
    queryFn: ({ pageParam = 1 }) =>
      getDonHistories({ ...params, transactionType, pageNo: pageParam }),
    getNextPageParam: ({ last, pageNo }) => (last ? undefined : pageNo + 1),
    ...options,
  });
};

export type RewardDetail = {
  level: number;
  itemType: 'CITIZEN' | 'PET' | string;
  count: number;
  amount: number;
  boost: number;
};
export type RewardHistory = {
  id: number;
  totalAmount: number;
  assetType: 'DON' | 'LUP' | string;
  saleType: 'B2C' | 'C2C' | string;
  purchaseAmount: number;
  purchaseCurrency: string;
  rewardAmount: number;
  purchaseCount: number;
  rewardAt: Date;
  rewardDetailList: RewardDetail[];
};

export const rewardHistoryUrl = '/api/citizen/v1/balance/transactions/rewards/:id';

const transformRewardHistory = (data: RewardHistory) => ({
  ...data,
  rewardAt: new Date(data.rewardAt),
});
export const getRewardHistory = async (id: number) => {
  const url = generatePath(rewardHistoryUrl, { id });
  const { data } = await axios.get<RewardHistory>(url);
  return transformRewardHistory(data);
};
export const useRewardHistory = (id: number, options?: UseDosiminQueryOptions<RewardHistory>) =>
  useQuery<RewardHistory>({
    queryKey: ['don', 'history', id],
    queryFn: () => getRewardHistory(id),
    initialData: {
      id,
      totalAmount: 0,
      assetType: 'DON',
      saleType: 'B2C',
      purchaseAmount: 0,
      purchaseCurrency: 'USD',
      rewardAmount: 0,
      purchaseCount: 0,
      rewardAt: new Date(),
      rewardDetailList: [],
    },
    ...options,
  });
